import React, { useState } from 'react';
import { Link as MuiLink, Stack, Typography } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { useTranslation, Trans } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { OrganizationType } from '../../models/Organization';
import { apiClient } from '../../publicApiClient';
import { schoolTutorPagePath } from '../../utils/publicSpaceResolution';
import { resolveTutorSchoolAffiliation } from '../../utils/tutorSchoolAffiliation';

export interface TutorSchoolAffiliationProps {
  organizationType?: OrganizationType | string | null;
  spaceName?: string | null;
  /** When set with spaceCity, builds a direct tutors link. */
  spaceId?: string | null;
  /** Space city for `/user/search/{city}/{spaceName}/tutors`. Optional if spaceId can be resolved. */
  spaceCity?: string | null;
  variant?: 'body2' | 'subtitle2';
}

const TutorSchoolAffiliation: React.FC<TutorSchoolAffiliationProps> = ({
  organizationType,
  spaceName,
  spaceId,
  spaceCity,
  variant = 'body2',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const affiliation = resolveTutorSchoolAffiliation(organizationType, spaceName);

  if (!affiliation) {
    return null;
  }

  const iconSize = variant === 'subtitle2' ? 18 : 16;

  const stackSx = { mt: 0.75, mb: 0.25 };

  if (affiliation.kind === 'individualTutor') {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={stackSx}
      >
        <SchoolOutlinedIcon sx={{ fontSize: iconSize, color: 'text.secondary' }} />
        <Typography variant={variant} color="text.secondary">
          {t('tutorSearch.individualTutor', 'Individual Tutor')}
        </Typography>
      </Stack>
    );
  }

  const directPath =
    spaceCity?.trim() && affiliation.name
      ? schoolTutorPagePath(spaceCity.trim(), affiliation.name)
      : null;
  const canLink = Boolean(directPath || spaceId?.trim());

  const linkSx = {
    color: 'primary.main',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    cursor: resolving ? 'wait' : 'pointer',
    verticalAlign: 'baseline',
  } as const;

  const stopCardNavigation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleResolveClick = async (event: React.MouseEvent) => {
    stopCardNavigation(event);
    event.preventDefault();
    const id = spaceId?.trim();
    if (!id || resolving) return;
    setResolving(true);
    try {
      const space = await apiClient.getSpaceSummary(id);
      navigate(schoolTutorPagePath(space.city, space.name || affiliation.name));
    } catch {
      // Keep the label visible; navigation simply does not happen.
    } finally {
      setResolving(false);
    }
  };

  const spaceLink = directPath ? (
    <MuiLink
      component={RouterLink}
      to={directPath}
      variant={variant}
      underline="always"
      onClick={stopCardNavigation}
      sx={linkSx}
    />
  ) : canLink ? (
    <MuiLink
      component="button"
      type="button"
      variant={variant}
      underline="always"
      disabled={resolving}
      onClick={handleResolveClick}
      sx={{
        ...linkSx,
        background: 'none',
        border: 0,
        padding: 0,
        font: 'inherit',
      }}
    />
  ) : (
    <Typography component="span" variant={variant} color="text.secondary" fontWeight={600} />
  );

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      sx={stackSx}
    >
      <SchoolOutlinedIcon sx={{ fontSize: iconSize, color: 'text.secondary' }} />
      <Typography variant={variant} color="text.secondary" component="span">
        <Trans
          i18nKey="tutorSearch.schoolAffiliation"
          defaults="Teaches at <spaceLink>{{name}}</spaceLink>"
          values={{ name: affiliation.name }}
          components={{ spaceLink }}
        />
      </Typography>
    </Stack>
  );
};

export default TutorSchoolAffiliation;
